import { useState, useRef, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Shield, Settings, User as UserIcon, LogOut, MailWarning, type LucideIcon } from 'lucide-react'
import { useUser } from '../../contexts/UserContext'
import { cn } from '../../lib/utils'

export interface UserMenuItem {
  label: string
  icon?: LucideIcon
  iconClassName?: string
  /** Draw a divider above this item. */
  separatorBefore?: boolean
  onClick: () => void
}

export interface AccountMenuProps {
  userName?: string
  userOfficeName?: string
  onLogout: () => void
  /** Compact (icon-only) rendering for a collapsed sidebar rail. */
  collapsed?: boolean
  /** Menu items above Logout (which is always shown). */
  items: UserMenuItem[]
  /** Shown at the top of the open menu, e.g. a warning about the account. */
  notice?: ReactNode
  /** A red dot on the avatar, to draw the eye to `notice`. */
  alert?: boolean
}

/**
 * Account dropdown for a sidebar footer, with no account source of its own:
 * the caller supplies the name, items and logout. UserMenu wraps it for the
 * Django-signed-in sections; Transit, which signs its users in itself, uses
 * it directly. Opens upward since it lives at the bottom of the sidebar.
 */
export const AccountMenu = ({ userName = 'User', userOfficeName = 'Office', onLogout, collapsed = false, items, notice, alert = false }: AccountMenuProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    if (open) {
      document.addEventListener('mousedown', onClick)
      document.addEventListener('keydown', onEsc)
    }
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const initials = userName.split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || 'U'

  const item = 'w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-popover-foreground hover:bg-accent hover:text-accent-foreground transition-colors'

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          // Left-anchored + fixed height so the avatar never jumps on collapse:
          // the footer padding leaves it centered in the rail when collapsed,
          // and the constant height keeps it from drifting up when the taller
          // two-line name block appears on expand. Only the text/chevron reveal.
          'flex items-center gap-2 w-full rounded-lg px-2 h-[52px] hover:bg-sidebar-accent transition-colors'
        )}
      >
        <div className="relative h-8 w-8 shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-semibold">
            {initials}
          </div>
          {alert && (
            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-destructive ring-2 ring-sidebar" />
          )}
        </div>
        {!collapsed && (
          <>
            <div className="min-w-0 flex-1 text-left">
              <div className="text-sm font-medium text-sidebar-foreground truncate">{userName}</div>
              <div className="text-xs text-muted-foreground truncate">{userOfficeName}</div>
            </div>
            <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
          </>
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute bottom-full left-0 mb-1 w-56 bg-popover border border-border rounded-md shadow-lg z-50 py-1"
        >
          {notice && (
            <>
              {notice}
              <div className="border-t border-border my-1" />
            </>
          )}
          {items.map((it) => {
            const Icon = it.icon
            return (
              <div key={it.label}>
                {it.separatorBefore && <div className="border-t border-border my-1" />}
                <button onClick={() => { setOpen(false); it.onClick() }} className={item}>
                  {Icon && <Icon className={cn('w-4 h-4', it.iconClassName)} />} {it.label}
                </button>
              </div>
            )
          })}
          {items.length > 0 && <div className="border-t border-border my-1" />}
          <button
            onClick={() => { setOpen(false); onLogout() }}
            className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      )}
    </div>
  )
}

export interface UserMenuProps extends Omit<AccountMenuProps, 'items' | 'notice' | 'alert'> {
  /** Override the default PM account items (Office/User settings, Admin Console).
   *  Used by sections like Gateway with a simpler menu. Logout is always shown. */
  items?: UserMenuItem[]
}

/**
 * AccountMenu for the Django-signed-in sections: the email-verification
 * warning, and by default the office/user settings and (superusers) the Admin
 * Console.
 */
const UserMenu = ({ items, ...props }: UserMenuProps) => {
  const navigate = useNavigate()
  const { user } = useUser()
  const emailUnverified = !!user && !user.email_verified_at

  const defaults: UserMenuItem[] = [
    { label: 'Office Settings', icon: Settings, onClick: () => navigate('/settings/office') },
    { label: 'User Settings', icon: UserIcon, onClick: () => navigate('/settings/user') },
    ...(user?.is_superuser
      ? [{ label: 'Admin Console', icon: Shield, iconClassName: 'text-primary', separatorBefore: true, onClick: () => navigate('/console') }]
      : []),
  ]

  return (
    <AccountMenu
      {...props}
      items={items ?? defaults}
      alert={emailUnverified}
      notice={emailUnverified && (
        <div className="flex items-start gap-2 px-3 py-2 text-xs text-warning">
          <MailWarning className="w-4 h-4 shrink-0 mt-0.5" />
          <span>Please verify your email — check your inbox for a confirmation link.</span>
        </div>
      )}
    />
  )
}

export default UserMenu
